import { WalletName } from "@solana/wallet-adapter-base";
import { useWallet as useSolanaWallet } from "@solana/wallet-adapter-react";
import { getAnalytics, logEvent } from "firebase/analytics";
import { useSnackbar } from "notistack";
import { FC, useCallback, useEffect, useMemo } from "react";

import {
  Button,
  IOption,
  Options,
  SnackMessage,
  Text,
  WalletOption,
} from "../../../components";
import { useNetworks } from "../../../hooks";
import { WalletItem } from "../../../interfaces";
import { shortAddress } from "../../../utils/utils";
import "./ConnectWalletModal.scss";

interface Props {
  onCloseClick: () => void;
  setSelectedSolanaWallet: (name: string | null) => void;
}

export const SolanaSection: FC<Props> = ({
  onCloseClick,
  setSelectedSolanaWallet,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const {
    networks: { solana },
  } = useNetworks();
  const analytics = getAnalytics();
  const { wallets, wallet, select, disconnect, connected } = useSolanaWallet();

  useEffect(() => {
    // Set on page load.
    setSelectedSolanaWallet(wallet?.adapter.name || null);
  }, [wallet, connected, setSelectedSolanaWallet]);

  const onWalletClick = useCallback(
    async (walletName: WalletName) => {
      try {
        select(walletName);
        onCloseClick();
        setSelectedSolanaWallet(walletName);
        logEvent(analytics, "wallet_connected_success", {
          name: walletName,
        });
      } catch (err: any) {
        enqueueSnackbar(
          <SnackMessage
            message={{
              text: `There was a problem while connecting to ${walletName} wallet. Error: ${
                err?.message || "unknown"
              }`,
              variant: "error",
            }}
          />,
        );
        onCloseClick();
        logEvent(analytics, "wallet_connected_fail", {
          name: walletName,
        });
      }
    },
    [analytics, enqueueSnackbar, onCloseClick, select, setSelectedSolanaWallet],
  );

  const solanaWallets = useMemo(
    () =>
      wallets.reduce(
        (acc, wallet) => ({
          ...acc,
          [wallet.adapter.name]: {
            name: wallet.adapter.name,
            logo: (
              <img
                src={wallet.adapter.icon}
                alt={wallet.adapter.name}
                width={24}
                height={24}
              />
            ),
            onClick: () => onWalletClick(wallet.adapter.name),
            address: "",
            disabled: false,
          },
        }),
        {} as { [walletName: string]: WalletItem },
      ),
    [wallets, onWalletClick],
  );

  const solanaWalletOptions: IOption[] = Object.values(solanaWallets).map(
    (wallet) => {
      return {
        id: wallet.name,
        content: <WalletOption wallet={wallet} />,
      };
    },
  );

  const disconnectSolanaWallet = useCallback(async () => {
    disconnect();
    setSelectedSolanaWallet(null);
  }, [disconnect, setSelectedSolanaWallet]);

  return solana.address ? (
    <div className="connect-wallet-modal__connection-info">
      <Text
        text={`Connected Wallet: <span>${shortAddress(solana.address)}</span>`}
      />

      <Button onClick={disconnectSolanaWallet} variant="OUTLINED">
        Disconnect
      </Button>
    </div>
  ) : (
    <div className="connect-wallet-modal__connection-info-options">
      <p className="connect-wallet-modal__connection-info-title">Solana</p>
      <Options
        id="SOLANA"
        handleChange={(name) => solanaWallets[name].onClick()}
        options={solanaWalletOptions}
      />
    </div>
  );
};
